import { makeAutoObservable, runInAction } from "mobx";
import { IAccountModel } from "../models/Account";
import { TYPE, rootContainer } from "../services/container/root";
import ManagersApprovalService from "../services/api/managers-approval-service";
import { IOption } from "../components/inputs/select-dropdown/select-dropdown";

export interface IManagerCredentials {
    manager_id: number,
    password: string,
}

/** responsible for loading once list of manages and maintaining selected managerId */
export class ManagersListStore {
    private _managersList: IAccountModel[] = [];
    private _isLoading: boolean = false;
    private _isLoaded: boolean = false;
    private _managerId?: number; 

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get _managersApprovalService(): ManagersApprovalService {
        return rootContainer.get<ManagersApprovalService>(TYPE.ManagersApprovalService);
    }

    public get isLoading(): boolean { return this._isLoading; }
    public getManagerId(): number|undefined { return this._managerId; }
    public setManagerId(v: number): void { this._managerId = v; }

    public getSelectedManagerOption(): IOption|undefined {
        const manager = this._managersList.find((manager) => manager.id === this._managerId);
        if(!manager) {
            return undefined;
        }
        return {
            value: manager.id,
            label: `${manager.first_name} ${manager.surname}`
        }
    }

    public getManagersOptionList(): IOption[] {
        return this._managersList.map((managerModel) => {
            return {
                value: managerModel.id,
                label: `${managerModel.first_name} ${managerModel.surname}`
            }
        })
    }

    public async load() {
        if (this._isLoading || this._isLoaded) {
            return;
        }
        
        try {
            this._isLoading = true;
            const managersList = await this._managersApprovalService.fetchManagers();
            runInAction(() => {
                this._managersList = managersList;
                this._isLoading = false;
                this._isLoaded = true;
            });
        }
        catch (e) {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }
}


interface IManagerCredentialsChanges extends Partial<IManagerCredentials> {};

/** responsible for collecting credentials from UI */
export class ManagerCredentialsStore {
    private _isValidating: boolean = false;
    private _credentials: IManagerCredentialsChanges = {};

    constructor(private _managersList: ManagersListStore) {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get _managersApprovalService(): ManagersApprovalService {
        return rootContainer.get<ManagersApprovalService>(TYPE.ManagersApprovalService);
    }

    public getPassword(): string|undefined { return this._credentials.password; }
    public setPassword(password: string|undefined) { this._credentials.password = password; }
    public isValidating(): boolean { return this._isValidating; }

    /** true when all the needed values exists */
    public isSet(): boolean {
        return this._managersList.getManagerId() !== undefined && !isEmpty(this._credentials.password);
    }

    public async validate(): Promise<boolean> {
        const creds = this.getCredentials();
        if (!creds) {
            return false;
        }

        let validationResponse;
        try {
            this._isValidating = true;
            validationResponse = await this._managersApprovalService.sendManagersCredentials(creds);
        } 
        catch (e) {   }

        runInAction(() => {
            this._isValidating = false;
        });
        return validationResponse?.success ?? false;
    }

    public getCredentials(): IManagerCredentials|undefined {
        if (!this.isSet()) {
            return undefined;
        }

        return {
            ...this._credentials,
            manager_id: this._managersList.getManagerId(),
        } as IManagerCredentials;
    }

}


function isEmpty(v?: string): boolean {
    return v === undefined || v === null || v.trim() === '';
}
